import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import isEmpty from 'lodash/isEmpty';

import './styles.scss';
import styles from './styles.css';
import uniqid from 'uniqid';
import usePerformanceTiers from 'chameleon/src/hooks/usePerformanceTiers';

const getPercentage = (value, dividend) => Math.round((value / dividend) * 100);

const getStatus = (percentage) => {
  if (percentage < 40) {
    return 'developing';
  } else if (percentage < 70) {
    return 'approaching';
  }
  return 'achieving';
};

const getScoreAndPercentageLine = (score, maxScore) => {
  const percentage = getPercentage(score, maxScore);

  const lessThanAverage = percentage < 25;

  if (!lessThanAverage) {
    const marginRight = `${100 - percentage}%`;
    return (
      <Fragment>
        <div className="content content__right">
          <span data-test-score-number className="dark performance-score">
            {score}/{maxScore}
          </span>
        </div>
        <div
          data-test-score-line
          className="percentage-line percentage-line__right"
          style={{ marginRight }}
        />
      </Fragment>
    );
  }
  const marginLeft = `${percentage}%`;
  return (
    <Fragment>
      <div
        data-test-score-line
        className="percentage-line percentage-line__left"
        style={{ marginLeft }}
      />
      <div className="content content__left">
        <span data-test-score-number className="dark performance-score">
          {score}/{maxScore}
        </span>
      </div>
    </Fragment>
  );
};

const StudentPerformanceBar = ({
  studentScore,
  subjectId,
  linkPath,
  linkAriaLabel,
  progress,
  small = false,
  linkProps = {},
  isTabbable = true,
}) => {
  const {
    learnosityReportMaxScore: maxScore,
    learnosityReportScoreParsed: score,
  } = studentScore;

  const { performanceTiers } = usePerformanceTiers(subjectId);

  const tierPercentages = performanceTiers.reduce((tiers, tier, index) => {
    tiers[index] = tier;
    return tiers;
  }, {});

  const percentage = getPercentage(score, maxScore);
  const status = getStatus(percentage);

  const id = `student-performance-bar-${uniqid()}`;
  /**
   * We effectively have 3 ways to determine if its teacher, student, or teacher and student scored
   * Sometimes the progress object comes back in snake case (1), while other times it's camelCase (2).
   * Other times we get this information from a submission object which has a status property that gives
   * us this info (3). Those are the 3 ways reflected in the logic below
   */
  let scoringLabel = 'Teacher Scored.';
  const scored = progress.scored || (progress.status === 'scored' ? 1 : 0);
  const studentScored =
    progress.student_scored ||
    progress.studentScored ||
    (progress.status === 'student_scored' ? 1 : 0);
  const teacherAndStudentScored =
    progress.teacher_and_student_scored ||
    progress.teacherAndStudentScored ||
    (progress.status === 'teacher_and_student_scored' ? 1 : 0);

  if (scored === 0 && teacherAndStudentScored === 0 && studentScored > 0) {
    scoringLabel = 'Student Scored.';
  } else if (teacherAndStudentScored > 0 || (scored > 0 && studentScored > 0)) {
    scoringLabel = 'Teacher and Student Scored.';
  }

  if (isEmpty(progress)) {
    scoringLabel = '';
  }

  const performanceBarAriaLabel = `${score} points out of ${maxScore} points, ${
    status || ''
  }. ${scoringLabel}`;
  const tiers = Object.keys(tierPercentages);
  const smallClass = small ? 'small' : '';
  const barIsLinked = linkPath || !isEmpty(linkProps);

  const performanceBar = (tabIndex = '0') => (
    <div
      data-test-loading
      className={`StudentPerformanceBar ${styles.performanceBarMinWidth} ${smallClass}`}
      id={id}
      {...(barIsLinked
        ? {}
        : {
            role: 'figure',
            tabIndex: isTabbable ? tabIndex : '-1',
            'aria-label': performanceBarAriaLabel,
          })}
    >
      <div className="StudentPerformanceBar-barContainer">
        {tiers.map((key) => {
          const tier = performanceTiers[key];
          return (
            <span
              className={`${tier.color} tier-bar ${smallClass}`}
              key={tier.type}
              style={{ width: `${100 / tiers.length}%` }}
            />
          );
        })}
      </div>
      <div className="content-container">
        {getScoreAndPercentageLine(score, maxScore)}
      </div>
    </div>
  );

  if (barIsLinked) {
    const props = {
      to: {
        pathname: linkPath,
      },
      ...linkProps,
    };

    return (
      <Link
        {...props}
        aria-label={`${
          Boolean(linkAriaLabel) ? `${linkAriaLabel}, ` : ''
        }${performanceBarAriaLabel}`}
      >
        {performanceBar(-1)}
      </Link>
    );
  }

  return performanceBar();
};

StudentPerformanceBar.propTypes = {
  studentScore: PropTypes.object.isRequired,
  linkPath: PropTypes.string,
  linkAriaLabel: PropTypes.string,
  progress: PropTypes.object,
};

StudentPerformanceBar.defaultProps = {
  linkPath: '',
  linkAriaLabel: '',
  progress: {},
};

export default connect((state) => ({
  subjectId: state.subject.data.id,
}))(StudentPerformanceBar);
